import {Booking, BookingProduct, BookingTime, DraftBooking} from 'services/entities/booking';
import {DateTime, TimeSpan} from 'util/time';
import {getSessionItem, removeSessionItem, setSessionItem} from 'util/session';
import {loadSpace} from 'services/spaces';
import {loadDesign} from 'services/designs';
import {loadBooking} from 'services/bookings/index';
import {loadSettings} from 'services/pricing';
import {validateDiscount} from 'services/discounts';
import {ProductSKU} from 'services/entities/products';
import {CurrencyAmount} from 'util/currency';
import {callAPI} from 'services/api';

const DRAFT_BOOKING_SESSION_KEY = 'booking.draft';

export async function createDraftBooking(space: string): Promise<DraftBooking> {
  return {
    code: null,
    space: space,
    design: null,
    times: [],
    products: [],
    discounts: []
  };
}

export function convertDraftBooking(booking: Booking): DraftBooking {
  const includeFlexiTicket = !!booking.products.find(p => p.sku === ProductSKU.FlexiTicket);
  return {
    code: booking.code,
    space: booking.space,
    design: booking.design,
    times: [{ startTime: booking.startTime.clone(), endTime: booking.endTime.clone(), includeFlexiTicket: includeFlexiTicket }],
    products: booking.products, // TODO: clone objects?
    discounts: booking.discounts.map(d => d.code)
  };
}

export async function loadDraftBooking(expectedCode?: string): Promise<DraftBooking|null> {
  const bookingData = getSessionItem(DRAFT_BOOKING_SESSION_KEY);
  if(!bookingData) {
    // no draft booking
    if(!expectedCode) {
      return null;
    } else {
      // load booking and convert to draft booking
      const booking = await loadBooking(expectedCode);
      return convertDraftBooking(booking);
    }
  } else if(expectedCode && bookingData.code !== expectedCode) {
    // booking code mismatch
    // load booking and convert to draft booking
    const booking = await loadBooking(expectedCode);
    return convertDraftBooking(booking);
  }
  let spaceCode = bookingData.space;
  if(spaceCode) {
    try {
      await loadSpace(spaceCode);
    } catch (err) {
      console.warn("Unable to load space", err);
      spaceCode = null;
    }
  }
  let designCode = bookingData.design;
  if(designCode) {
    try {
      await loadDesign(designCode);
    } catch (err) {
      console.warn("Unable to load design", err);
      designCode = null;
    }
  }
  const settings = await loadSettings();
  const minDate = DateTime.today(settings.defaultTimeZone).add(new TimeSpan({days: settings.minDateOffset}));
  const times: BookingTime[] = [];
  const timesData = bookingData.times;
  if(Array.isArray(timesData)) {
    for (const t of timesData) {
      let start = parseBookingDate(t.startTime);
      let end = parseBookingDate(t.endTime);
      if(!start || start.lessThan(minDate)) {
        start = minDate;
      }
      if(!end || end.lessThan(minDate)) {
        end = minDate;
      }
      times.push({startTime: start, endTime: end, includeFlexiTicket: !!t.includeFlexiTicket});
    }
  }
  const products: BookingProduct[] = bookingData.products || [];
  for(const pr of products) {
    if(!pr.optionValues) {
      pr.optionValues = {};
    }
    pr.quantity = pr.quantity || 0;
  }
  const discountCodes: string[] = bookingData.discounts || [];
  return {
    code: bookingData.code,
    space: spaceCode,
    design: designCode,
    times: times,
    products: products,
    discounts: discountCodes
  };
}

function parseBookingDate(date: any): DateTime|null {
  if(!date) {
    return null;
  } else if(typeof(date) === 'string') {
    return DateTime.tryParse(date);
  } else if(date instanceof DateTime) {
    return date;
  } else {
    return null;
  }
}

export function saveDraftBooking(booking: DraftBooking) {
  setSessionItem(DRAFT_BOOKING_SESSION_KEY, booking);
}

export function deleteDraftBooking() {
  removeSessionItem(DRAFT_BOOKING_SESSION_KEY);
}

export function getDraftBookingProductQuantity(booking: DraftBooking, sku: string): number {
  const bookingProduct = booking.products.find(pr => pr.sku === sku);
  return bookingProduct ? bookingProduct.quantity : 0;
}

export function setDraftBookingProductQuantity(booking: DraftBooking, sku: string, quantity: number) {
  let bookingProduct = booking.products.find(pr => pr.sku === sku);
  if(quantity > 0) {
    if(!bookingProduct) {
      bookingProduct = { sku: sku, quantity: quantity, optionValues: {} };
      booking.products.push(bookingProduct);
    } else {
      bookingProduct.quantity = quantity;
    }
  } else if(bookingProduct) {
    // don't delete product - might have option values selected
    bookingProduct.quantity = 0;
  }
}

export function getDraftBookingProductOption(booking: DraftBooking, sku: string, option: string): string|null {
  const bookingProduct = booking.products.find(pr => pr.sku === sku);
  return bookingProduct ? bookingProduct.optionValues[option] || null : null;
}

export function setDraftBookingProductOption(booking: DraftBooking, sku: string, option: string, value: string|null) {
  let bookingProduct = booking.products.find(pr => pr.sku === sku);
  if(value) {
    if(!bookingProduct) {
      bookingProduct = { sku: sku, quantity: 0, optionValues: {} };
      bookingProduct.optionValues[option] = value;
      booking.products.push(bookingProduct);
    } else {
      bookingProduct.optionValues[option] = value;
    }
  } else if(bookingProduct) {
    delete bookingProduct.optionValues[option];
  }
}

export async function addDraftBookingDiscount(booking: DraftBooking, code: string): Promise<any> {
  if(booking.discounts.find(d => d.toLowerCase() === code.toLowerCase())) {
    return;
  }
  const discount = await validateDiscount(code);
  booking.discounts = [discount.code];
}

export function removeDraftBookingDiscount(booking: DraftBooking, code: string) {
  const discountIndex = booking.discounts.findIndex(d => d.toLowerCase() === code.toLowerCase());
  if(discountIndex >= 0) {
    booking.discounts.splice(discountIndex, 1);
  }
}

export function isDraftBooking(booking: DraftBooking|Booking): boolean {
  return 'times' in booking;
}

export async function generateDraftBookingOccupationalLicense(booking: DraftBooking, amount: CurrencyAmount|null): Promise<Blob> {
  const time = booking.times[0];
  const startTime = time.startTime.format('YYYY-MM-DD HH:mm:ss');
  const endTime = time.endTime.format('YYYY-MM-DD HH:mm:ss');
  const url = `/bookings/occupational-license/?space=${booking.space}&start_time=${startTime}&end_time=${endTime}&amount=${amount?.format('0.00')}`;
  return await callAPI(url, 'GET', null, true);
}
